import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useEffect } from 'react';
import { Form } from '@components/Form';
import MDButton from '@components/MDButton';
import { smallDialog } from 'assets/theme/dialogSize';
import { setLoading, useMaterialUIController } from 'context';
import { CrmColumnService } from '../../services/crm-column.service';
import { STAGE_NAME_MAX_LENGTH } from '../../constants/stage-name';
import { BoardColumnWithLeads } from '../../services/responses/board-column.response';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { firstErrorMessage } from '@functions/first-error-message';

interface Props {
    open: boolean;
    boardId: string;
    columnCount: number;
    column?: BoardColumnWithLeads | null;
    onClose: () => void;
    onSaved: () => void;
}

export const CrmColumnModal = ({ open, boardId, columnCount, column, onClose, onSaved }: Props) => {
    const { t } = useTranslation();
    const [, dispatch] = useMaterialUIController();
    const { showAlert } = useAlert();
    const columnService = new CrmColumnService();

    const schema = z.object({
        name: z.string().min(1, t('validacao.obrigatorio')).max(STAGE_NAME_MAX_LENGTH),
    });

    type ColumnForm = z.infer<typeof schema>;
    const form = useForm<ColumnForm>({
        resolver: zodResolver(schema),
        defaultValues: { name: '' },
    });

    const { handleSubmit, reset, formState: { errors } } = form;

    useEffect(() => {
        if (open) {
            reset({ name: column?.name ?? '' });
        }
    }, [open, column]);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            showAlert(firstErrorMessage(errors) ?? t('general.camposObrigatorios'), 'error', 2500);
        }
    }, [errors]);

    const saveChanges = async (data: ColumnForm) => {
        setLoading(dispatch, true);
        try {
            if (column) {
                await columnService.update(column.id, { name: data.name.trim() });
                showAlert(t('general.atualizacao'), 'success', 2000);
            } else {
                await columnService.create({ boardId, name: data.name.trim(), sortOrder: columnCount });
                showAlert(t('general.cadastro'), 'success', 2000);
            }
            onSaved();
            onClose();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(dispatch, false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
            <DialogTitle>{column ? t('crmKanban.editarColuna') : t('crmKanban.novaColuna')}</DialogTitle>
            <FormProvider {...form}>
                <form onSubmit={handleSubmit(saveChanges)}>
                    <DialogContent style={smallDialog}>
                        <Grid container spacing={2}>
                            <Form.Field size={12}>
                                <FormControl fullWidth>
                                    <Form.Label>
                                        {t('general.nome')}
                                        <Form.MandatoryIcon />
                                    </Form.Label>
                                    <Form.Input type="text" autoComplete="off" size="medium" name="name" autoFocus inputProps={{ maxLength: STAGE_NAME_MAX_LENGTH }} />
                                    <Form.ErrorMessage field="name" />
                                </FormControl>
                            </Form.Field>
                        </Grid>
                    </DialogContent>
                    <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box></Box>
                        <Box>
                            <MDButton sx={{ marginRight: '5px' }} variant="gradient" color="dark" onClick={onClose}>
                                {t('general.cancelar')}
                            </MDButton>
                            <MDButton variant="gradient" color="success" type="submit">
                                {t('general.salvar')}
                            </MDButton>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};
